// packages/shared/src/rng.ts — seeded RNG (§7.6). Bit-exact on every JS engine: only Math.imul, >>>, |0, ^.
export interface Rng {
  next(): number;                                   // [0, 1)
  u32(): number;                                    // [0, 2^32)
  int(min: number, maxInclusive: number): number;
  pick<T>(items: readonly T[]): T;
  weighted<T>(items: readonly { w: number; v: T }[]): T;
  shuffle<T>(items: T[]): T[];                      // in place, Fisher–Yates
  chance(p: number): boolean;
  fork(...salt: (string | number)[]): Rng;          // independent child stream
}
const key = (parts: readonly (string | number)[]) => parts.map(String).join('|');
export function cyrb128(str: string): [number, number, number, number] {
  let h1 = 1779033703, h2 = 3144134277, h3 = 1013904242, h4 = 2773480762;
  for (let i = 0; i < str.length; i++) {
    const k = str.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  h1 ^= h2 ^ h3 ^ h4; h2 ^= h1; h3 ^= h1; h4 ^= h1;
  return [h1 >>> 0, h2 >>> 0, h3 >>> 0, h4 >>> 0];
}
export function sfc32(a: number, b: number, c: number, d: number): () => number {
  return () => {
    a >>>= 0; b >>>= 0; c >>>= 0; d >>>= 0;
    const t = (((a + b) | 0) + d) | 0;
    d = (d + 1) | 0;
    a = b ^ (b >>> 9);
    b = (c + (c << 3)) | 0;
    c = (c << 21) | (c >>> 11);
    c = (c + t) | 0;
    return t >>> 0;
  };
}

class SeededRng implements Rng {
  private readonly gen: () => number;
  private readonly seedKey: string;
  constructor(seedKey: string) {
    this.seedKey = seedKey;
    const [a, b, c, d] = cyrb128(seedKey);
    this.gen = sfc32(a, b, c, d);
    for (let i = 0; i < 12; i++) this.gen();
  }
  u32(): number { return this.gen(); }
  next(): number { return this.gen() / 4294967296; }
  int(min: number, maxInclusive: number): number {
    if (!Number.isInteger(min) || !Number.isInteger(maxInclusive) || min > maxInclusive) throw new Error(`int: bad range ${min}..${maxInclusive}`);
    return min + Math.floor(this.next() * (maxInclusive - min + 1));
  }
  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('pick from an empty list');
    return items[Math.floor(this.next() * items.length)] as T;
  }
  weighted<T>(items: readonly { w: number; v: T }[]): T {
    const total = items.reduce((s, i) => s + Math.max(0, i.w), 0);
    if (total <= 0) throw new Error('weighted: no positive weight');
    let r = this.next() * total;
    for (const i of items) { r -= Math.max(0, i.w); if (r < 0) return i.v; }
    return items[items.length - 1]!.v;
  }
  shuffle<T>(items: T[]): T[] {
    for (let i = items.length - 1; i > 0; i--) { const j = Math.floor(this.next() * (i + 1)); [items[i], items[j]] = [items[j]!, items[i]!]; }
    return items;
  }
  chance(p: number): boolean { return this.next() < p; }
  /** Reseeds from the parent's seed key, so it never consumes the parent stream. */
  fork(...salt: (string | number)[]): Rng { return new SeededRng(key([this.seedKey, 'fork', ...salt])); }
}
/** Seeds from the joined parts, then discards 12 outputs. */
export function rngFrom(...parts: (string | number)[]): Rng { return new SeededRng(key(parts)); }
export function hash32(...parts: (string | number)[]): number { return cyrb128(key(parts))[0]; }
